import MojitoServices from 'mojito/services';
import { useMemo } from 'react';
import { ALL_SPORTS_ITEM_ID } from 'presentation/components/sports-filter/hooks/index.js';
import { useSelector } from 'react-redux';
import EventCardsCarouselUtils from 'modules/event-cards-carousel/utils.js';
import EventCardsTypes from 'modules/event-cards/types.js';
import { promotionMatchesPrebuiltMarketType } from 'modules/promotions/prebuilt-promotion-cards/utils.js';
import { useSortedItems } from 'modules/common/hooks';
import { pickBy, without } from 'mojito/utils';

const { PROP } = EventCardsTypes.CARD_TYPE;

const { convertToCard } = EventCardsCarouselUtils;
const { makeSelectEventsState, makeSelectMarketsState, selectMarkets } =
    MojitoServices.SportsContent.Events.selectors;
const { types: ServicesTypes } = MojitoServices.Common;
const { UNAVAILABLE } = ServicesTypes.CONTENT_STATE;

const getSportId = obj => obj.sportId;

const useCards = (promotions, priorityType) => {
    const promotionCards = useMemo(
        () => (promotions ? promotions.map(convertToCard) : []),
        [promotions]
    );

    return useSortedItems(promotionCards, priorityType, getSportId);
};

/**
 * Contains custom hooks for event card carousel functionality.
 *
 * @class EventCardsCarouselHooks
 * @name hooks
 * @memberof Mojito.Modules.EventCardsCarousel
 */

/**
 * Converts promotions to cards, sort by display order and filter out cards which have events with an UNAVAILABLE state.
 * Prop cards with an unavailable market are also removed because PropCard renders nothing in that case.
 * NOTE: Not to be used to convert promotions to pre built bet cards, instead use
 * {@link Mojito.Modules.EventCardsCarousel.hooks.usePreBuiltBetCardsPromotions|usePreBuiltBetCardsPromotions}.
 *
 * @function useCardsPromotions
 *
 * @param {Array<Mojito.Services.Promotions.types.Promotion>} promotions - Promotions list.
 * @param {Mojito.Services.UserSettings.types.PRIORITY_TYPES} priorityType - Priority to be used for sorting.
 *
 * @returns {Array<object>} List of cards.
 * @memberof Mojito.Modules.EventCardsCarousel.hooks
 */
export const useCardsPromotions = (promotions, priorityType) => {
    const cards = useCards(promotions, priorityType);
    const eventIds = useMemo(
        () =>
            cards
                .flatMap(card => (card.content.eventId ? [card.content.eventId] : []))
                .filter(Boolean),
        [cards]
    );
    const propMarketIds = useMemo(
        () =>
            cards
                .filter(card => card.type === PROP && card.content.marketId)
                .map(card => card.content.marketId),
        [cards]
    );

    const selectEventsState = useMemo(makeSelectEventsState, []);
    const eventsState = useSelector(state => selectEventsState(eventIds, state));
    const selectMarketsState = useMemo(makeSelectMarketsState, []);
    const marketsState = useSelector(state => selectMarketsState(propMarketIds, state));

    // Sometimes promotions can still have event card for outdated event. It will happen if event is over but CmsJob hasn't rerun yet.
    // Here we will detect such event cards to ignore them while rendering.
    return useMemo(() => {
        const unavailableEventIds = Object.keys(
            pickBy(eventsState, eventState => eventState === UNAVAILABLE)
        );
        const unavailableMarketIds = Object.keys(
            pickBy(marketsState, marketState => marketState === UNAVAILABLE)
        );

        return cards.filter(card => {
            if (unavailableEventIds.includes(card.content.eventId)) {
                return false;
            }
            if (card.type === PROP && unavailableMarketIds.includes(card.content.marketId)) {
                return false;
            }
            return true;
        });
    }, [eventsState, marketsState, cards]);
};

/**
 * Converts promotions to pre built bet cards, sort by display order and filter
 * out cards which have event or market with an UNAVAILABLE state.
 * This is needed instead of {@link Mojito.Modules.EventCardsCarousel.hooks.useCardsPromotions|useCardsPromotions}
 * because pre built bet cards don't support being shown in suspended state.
 *
 * @function usePreBuiltBetCardsPromotions
 *
 * @param {Array<Mojito.Services.Promotions.types.Promotion>} promotions - Promotions list.
 * @param {Mojito.Services.UserSettings.types.PRIORITY_TYPES} priorityType - Priority to be used for sorting.
 * @param {string} [prebuiltMarketType] - Carousel filter (`PBBB` = bet builder markets PBBB/PBMK/PBMF, `PBAC` = accas). See {@link Mojito.Modules.Promotions.PrebuiltPromotionCards.utils#promotionMatchesPrebuiltMarketType}.
 *
 * @returns {Array<object>} List of cards.
 * @memberof Mojito.Modules.EventCardsCarousel.hooks
 */
export const usePreBuiltBetCardsPromotions = (promotions, priorityType, prebuiltMarketType) => {
    let cards = useCards(promotions, priorityType);
    const marketsById = useSelector(selectMarkets);

    const promotionsById = useMemo(
        () => Object.fromEntries((promotions || []).map(promotion => [promotion.id, promotion])),
        [promotions]
    );

    cards = useMemo(() => {
        if (!prebuiltMarketType) {
            return cards;
        }

        return cards.filter(card =>
            promotionMatchesPrebuiltMarketType(
                promotionsById[card.id],
                prebuiltMarketType,
                marketsById[card.content.marketId]
            )
        );
    }, [cards, prebuiltMarketType, promotionsById, marketsById]);

    const eventIds = useMemo(() => cards.map(card => card.content.eventId), [cards]);
    const marketIds = useMemo(() => cards.map(card => card.content.marketId), [cards]);
    const selectEventsState = useMemo(makeSelectEventsState, []);
    const eventsState = useSelector(state => selectEventsState(eventIds, state));

    const selectMarketsState = useMemo(makeSelectMarketsState, []);
    const marketsState = useSelector(state => selectMarketsState(marketIds, state));

    // PreBuiltBetBuilderPromotions can have outdated event or market.
    // It will happen if event or market is over but CmsJob hasn't rerun yet.
    // Here we will detect such promotions to ignore them while rendering.
    return useMemo(() => {
        const unavailableEventIds = Object.keys(
            pickBy(eventsState, eventState => eventState === UNAVAILABLE)
        );
        const unavailableMarketIds = Object.keys(
            pickBy(marketsState, marketState => marketState === UNAVAILABLE)
        );

        const unavailableCards = cards.filter(
            card =>
                unavailableEventIds.includes(card.content.eventId) ||
                unavailableMarketIds.includes(card.content.marketId)
        );
        return unavailableCards.length ? without(cards, ...unavailableCards) : cards;
    }, [cards, eventsState, marketsState]);
};

/**
 * Keeps the cards of the given sport. Cards without a sport are dropped while a sport is given.
 *
 * @function useCardsSportFilter
 *
 * @param {Array<object>} cards - Cards holding a sportId.
 * @param {string} [sportId] - Sport to keep the cards of. Every card is kept when empty or `all`.
 *
 * @returns {Array<object>} Cards of the given sport.
 * @memberof Mojito.Modules.EventCardsCarousel.hooks
 */
export const useCardsSportFilter = (cards, sportId) =>
    useMemo(() => {
        if (!sportId || sportId === ALL_SPORTS_ITEM_ID) {
            return cards;
        }
        return cards.filter(card => card.sportId === sportId);
    }, [cards, sportId]);
